/*
 * Collection + search results: filter & sort form and pagination.
 * Results are swapped in through the Section Rendering API so the page never fully reloads;
 * every URL stays a normal shareable link and the back button works.
 * Phones: the form is a bottom sheet with an "apply" button. Desktop: the same form is a sidebar and applies on change.
 */

const desktop = window.matchMedia('(min-width: 1000px)');

class FacetsResults extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-facets-form]');
      if (!form) return;
      event.preventDefault();
      this.load(this.urlFromForm(form));
    });
    this.addEventListener('click', (event) => {
      const link = event.target.closest('a[data-facets-link]');
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey) return;
      event.preventDefault();
      this.load(link.href, { scroll: link.closest('.pagination') !== null });
    });
    this.addEventListener('change', (event) => {
      // Sidebar (desktop): every change applies straight away
      const form = event.target.closest('[data-facets-form]');
      if (form && desktop.matches && !event.target.matches('[data-range-min], [data-range-max]')) {
        clearTimeout(this.applyTimer);
        this.applyTimer = setTimeout(() => this.load(this.urlFromForm(form)), 150);
      }
    });
    window.addEventListener('popstate', () => this.load(window.location.href, { push: false }));
  }

  urlFromForm(form) {
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form)) {
      if (value === '') continue;
      params.append(key, value);
    }
    const query = params.toString();
    return form.action.split('?')[0] + (query ? `?${query}` : '');
  }

  async load(url, { push = true, scroll = false } = {}) {
    const open = this.querySelector('dialog[open]');
    if (open) (window.Cosmo?.closeDrawer || ((d) => d.close()))(open);
    // Remember which bands were open so the sidebar does not jump closed after every change
    const openGroups = [...this.querySelectorAll('.facets__group[open]')].map((d) => d.dataset.group);
    const closedGroups = [...this.querySelectorAll('.facets__group:not([open])')].map((d) => d.dataset.group);
    this.classList.add('is-loading');
    try {
      const target = new URL(url, window.location.origin);
      target.searchParams.set('section_id', this.sectionId);
      const html = await fetch(target).then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); });
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('facets-results');
      if (!fresh) throw new Error('missing results');
      this.innerHTML = fresh.innerHTML;
      this.querySelectorAll('.facets__group').forEach((d) => {
        if (openGroups.includes(d.dataset.group)) d.open = true;
        else if (closedGroups.includes(d.dataset.group)) d.open = false;
      });
      if (push) window.history.pushState({ facets: true }, '', url);
      if (scroll) this.querySelector('.results__layout')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    } catch (error) {
      window.location.href = url;
    } finally {
      this.classList.remove('is-loading');
    }
  }
}
if (!customElements.get('facets-results')) customElements.define('facets-results', FacetsResults);

// Price: two sliders on one track, kept in sync with the number boxes.
// On desktop, letting go of a slider applies the price.
class PriceRange extends HTMLElement {
  connectedCallback() {
    const max = Number(this.dataset.max);
    const minSlider = this.querySelector('[data-range-min]');
    const maxSlider = this.querySelector('[data-range-max]');
    const minField = this.querySelector('[data-number-min]');
    const maxField = this.querySelector('[data-number-max]');
    const fromSliders = (event) => {
      let low = Number(minSlider.value);
      let high = Number(maxSlider.value);
      if (low > high) {
        if (event.target === minSlider) low = high; else high = low;
        minSlider.value = low;
        maxSlider.value = high;
      }
      minField.value = low > 0 ? low : '';
      maxField.value = high < max ? high : '';
    };
    const fromFields = () => {
      minSlider.value = minField.value || 0;
      maxSlider.value = maxField.value || max;
    };
    const commit = () => { if (desktop.matches) minField.dispatchEvent(new Event('change', { bubbles: true })); };
    minSlider.addEventListener('input', fromSliders);
    maxSlider.addEventListener('input', fromSliders);
    minSlider.addEventListener('change', commit);
    maxSlider.addEventListener('change', commit);
    minField.addEventListener('input', fromFields);
    maxField.addEventListener('input', fromFields);
  }
}
if (!customElements.get('price-range')) customElements.define('price-range', PriceRange);
